import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, CHAT_READ_CURSOR_COMMANDS } from 'cpool-constants';
import { deriveChatReadCursorId } from 'cpool-utils';

import { APICommunicator } from '~/api/APICommunicator';
import { invalidateInboxUnreadQueries } from '~/features/whatsapp/utils/invalidateInboxUnreadQueries';
import useUserInfo from '~/hooks/useUserInfo';

// Targets ChatReadCursor, not Chat. Always pass the latest *inbound* message id — the cursor is
// a watermark over inbound rows only. `allowNoop` prevents a ZodError when re-marking the same watermark.
export const useMarkChatViewedMutation = (chatId: string) => {
    const queryClient = useQueryClient();
    const user = useUserInfo();

    return useMutation({
        mutationFn: (lastSeenMessageId: string) => {
            if (!user) throw new Error('No authenticated user');
            const readCursorId = deriveChatReadCursorId(chatId, user.id);
            return APICommunicator.sendCommand(
                {
                    aggregateId: readCursorId,
                    aggregateName: AGGREGATE_NAMES.CHAT_READ_CURSOR,
                    commandName: CHAT_READ_CURSOR_COMMANDS.markChatViewed,
                    payload: { chatId, lastSeenMessageId, userId: user.id },
                },
                { allowNoop: true },
            );
        },
        // Silent by design — fires on every mount/new message; skip useChainAwareInvalidation
        // as the chain includes manta's read-receipt round-trip and would delay the badge clear.
        onSuccess: () => {
            if (!user) return;
            invalidateInboxUnreadQueries(queryClient, user.id);
        },
    });
};
